import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, FolderOpen, Plus, Pencil, Archive, Store, Globe, Lock, GripVertical, Sparkles, Tablet } from 'lucide-react';
import RecipeListRow from './RecipeListRow';
import { MarzeBunka, NakladCenaBunka, ProdejBunka, StavBunka } from './PrehledBunky';
import { GRID } from './prehledGrid';
import RecipeKatIcon from './RecipeKatIcon';
import RecipeCategoryModal from './RecipeCategoryModal';
import { RecipeValidaceCounts } from './RecipeValidaceBadge';
import { recVariantKey, aggregateValidace } from './receptUtils';
import { nastavNahledTazeni } from './useVyberPolozek';

const BEZ_KAT = '__bez__';
const ROOT_DROP = '__root__'; // drop cíl pro přesun kategorie na kořenovou úroveň
const INDENT = 15; // px na úroveň stromu

// Abecední seřazení stromu (rekurzivně)
// Podle pořadí (kde jde řadit — pořadí přebírá pokladna i e-shop), jinak abecedně.
const sortTree = (nodes, podlePoradi = false) => (nodes || [])
    .map(n => ({ ...n, children: n.children?.length ? sortTree(n.children, podlePoradi) : [] }))
    .sort((a, b) => (podlePoradi ? (a.poradi ?? 0) - (b.poradi ?? 0) : 0) || (a.nazev || '').localeCompare(b.nazev || '', 'cs'));

// Najdi uzel v stromu dle id (rekurzivně)
const findNodeById = (nodes, id) => {
    for (const n of nodes || []) {
        if (n.id === id) return n;
        const f = findNodeById(n.children, id);
        if (f) return f;
    }
    return null;
};

// Množina id celého podstromu (vč. uzlu) — pro ochranu proti přesunu pod vlastního potomka
const subtreeIds = (node, acc = new Set()) => {
    acc.add(node.id);
    for (const ch of node.children || []) subtreeIds(ch, acc);
    return acc;
};

// Stromová navigace kategorií propojená se seznamem — inline editace + drag&drop položek.
export default function RecipeCategoryTree({
    katStrom, items, recGrouped, activeKey, activeId, onSelect,
    searchActive, isDark, mutedColor, secondaryColor,
    expanded, setExpanded,
    onCatSave, onCatAdd, onCatDelete, onMoveItems, onReparentCat, canEdit = true,
    // Řazení kategorií přetažením nad/pod jinou (src, cil, 'nad'|'pod'); bez něj jen vnořování.
    onReorderCat = null,
    vybrane, onKlikRadek, renderRow, zobrazitViditelnost = true,
    onAiAnalyza = null, siroky = false,
    skrytPridatKategorii = false, onRegisterNovaKategorie = null,
    onKategorieKlik = null, prodejMap = null, sloupce = null,
    // Vlastní souhrnné buňky kategorie v přehledu (karty materiálu mají jiné
    // sloupce než receptury). Dostane `{ node, polozky }` — aktivní položky
    // celého podstromu; bez něj se kreslí sloupce receptáře.
    renderKategorieBunky = null,
    // Mobil — služební řádky stromu („Bez kategorie", „Archív položek") musí
    // mít výšku na prst, ne na myš.
    kompaktni = false,
    // Úprava kategorie jako detail (standard §1i): tužka zavolá tohle místo
    // modalu a otevřená kategorie se v seznamu zvýrazní. Bez nich modal.
    onUpravitKategorii = null, aktivniKategorieId = null,
}) {
    const KANALY_ID = ['bistro', 'rozvoz', 'vydej'];
    const strom = useMemo(() => sortTree(katStrom, !!onReorderCat), [katStrom, onReorderCat]);

    // modal: null | { mode: 'create'|'edit', node?, parentId?, parentNazev? }
    const [modal, setModal] = useState(null);
    const [dragItemIds, setDragItemIds] = useState(null);   // táhnou se položky (jedna nebo celý výběr)
    const [dragCatId, setDragCatId] = useState(null);   // táhne se KATEGORIE (změna nadřazenosti)
    const [dragOverCat, setDragOverCat] = useState(null);
    // Kam padne tažená kategorie: 'nad' / 'pod' = řazení (čára), 'do' = vnoření.
    const [dragPoloha, setDragPoloha] = useState('do');
    const [archivOpen, setArchivOpen] = useState(false);

    const toggle = useCallback((id) => {
        setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }, [setExpanded]);

    // Klik na kategorii = „ukaž mi tuhle větev v přehledu". Když byl otevřený
    // detail, zavře se (seznam se roztáhne) a kategorie se rozbalí — sbalit ji
    // v tu chvíli by bylo proti smyslu kliknutí. Jinak je to obyčejný toggle.
    const klikNaKategorii = (node) => {
        const vysledek = onKategorieKlik?.(node);
        if (vysledek === 'blokovano') return;
        if (vysledek === 'prehled') { setExpanded(p => { const n = new Set(p); n.add(node.id); return n; }); return; }
        toggle(node.id);
    };

    const openEdit = (node) => {
        if (!onUpravitKategorii) { setModal({ mode: 'edit', node }); return; }
        setExpanded(p => { const n = new Set(p); n.add(node.id); return n; }); // ať jsou vidět produkty, kterých se úprava týká
        onUpravitKategorii(node);
    };
    const openCreate = useCallback((parentNode) => {
        if (parentNode) setExpanded(p => { const n = new Set(p); n.add(parentNode.id); return n; }); // ať je nová podkategorie vidět
        setModal({ mode: 'create', parentId: parentNode?.id ?? null, parentNazev: parentNode?.nazev ?? null });
    }, [setExpanded]);

    // Norma: „přidat kategorii" žije v hlavičce seznamu, ne dole pod stromem.
    // Modal ale patří sem, tak si hlavička vyzvedne otvírák.
    useEffect(() => { onRegisterNovaKategorie?.(() => openCreate(null)); }, [onRegisterNovaKategorie, openCreate]);

    const { itemsByKat, archiv } = useMemo(() => {
        const m = new Map();
        const arch = [];
        const known = new Set();
        const collect = (nodes) => { for (const n of nodes || []) { known.add(n.id); if (n.children?.length) collect(n.children); } };
        collect(strom);
        for (const it of items) {
            if (it.aktivni === false) { arch.push(it); continue; } // neaktivní → archiv
            const kid = (it.kategorie_id != null && known.has(it.kategorie_id)) ? it.kategorie_id : BEZ_KAT;
            if (!m.has(kid)) m.set(kid, []);
            m.get(kid).push(it);
        }
        return { itemsByKat: m, archiv: arch };
    }, [items, strom]);

    const countMap = useMemo(() => {
        const cm = new Map();
        const walk = (node) => {
            let c = itemsByKat.get(node.id)?.length || 0;
            for (const ch of node.children || []) c += walk(ch);
            cm.set(node.id, c);
            return c;
        };
        for (const n of strom) walk(n);
        return cm;
    }, [strom, itemsByKat]);

    // Kumulace validačních stavů přes celý podstrom kategorie (red/orange/green)
    const validaceMap = useMemo(() => {
        const vm = new Map();
        const walk = (node) => {
            const acc = { red: 0, orange: 0, green: 0 };
            for (const it of itemsByKat.get(node.id) || []) {
                const s = it?.validace?.stav;
                if (s && acc[s] != null) acc[s]++;
            }
            for (const ch of node.children || []) {
                const cc = walk(ch);
                acc.red += cc.red; acc.orange += cc.orange; acc.green += cc.green;
            }
            vm.set(node.id, acc);
            return acc;
        };
        for (const n of strom) walk(n);
        return vm;
    }, [strom, itemsByKat]);

    // Souhrn za podstrom — kategorie drží hodnoty ve stejných sloupcích jako
    // položky, takže se dá sjet očima jeden sloupec dolů a vidět, která
    // kategorie táhne marži dolů, aniž bys ji rozbaloval.
    const souhrnMap = useMemo(() => {
        const m = new Map();
        const prazdny = () => ({
            nakladSum: 0, nakladPocet: 0,
            marzeSum: { bistro: 0, rozvoz: 0, vydej: 0 },
            marzePocet: { bistro: 0, rozvoz: 0, vydej: 0 },
            prodej: 0,
        });

        const walk = (node) => {
            const acc = prazdny();

            for (const it of itemsByKat.get(node.id) || []) {
                if (it.total_cost != null) { acc.nakladSum += it.total_cost; acc.nakladPocet++; }
                for (const k of KANALY_ID) {
                    const d = it.marze_kanaly?.[k];
                    if (d && d.cena > 0 && d.pct != null) { acc.marzeSum[k] += d.pct; acc.marzePocet[k]++; }
                }
                acc.prodej += prodejMap?.[it.id]?.celkem || 0;
            }

            for (const ch of node.children || []) {
                const c = walk(ch);
                acc.nakladSum += c.nakladSum; acc.nakladPocet += c.nakladPocet;
                acc.prodej += c.prodej;
                for (const k of KANALY_ID) { acc.marzeSum[k] += c.marzeSum[k]; acc.marzePocet[k] += c.marzePocet[k]; }
            }

            m.set(node.id, {
                ...acc,
                naklad: acc.nakladPocet ? acc.nakladSum / acc.nakladPocet : null,
                marze: Object.fromEntries(KANALY_ID.map(k => [k, acc.marzePocet[k] ? acc.marzeSum[k] / acc.marzePocet[k] : null])),
            });
            return acc;
        };

        for (const n of strom) walk(n);
        return m;
    }, [strom, itemsByKat, prodejMap]);

    // Aktivní položky celého podstromu — jen pro vlastní souhrnné buňky.
    const podstromMap = useMemo(() => {
        if (!renderKategorieBunky) return null;
        const m = new Map();
        const walk = (node) => {
            const acc = [...(itemsByKat.get(node.id) || [])];
            for (const ch of node.children || []) acc.push(...walk(ch));
            m.set(node.id, acc);
            return acc;
        };
        for (const n of strom) walk(n);
        return m;
    }, [renderKategorieBunky, strom, itemsByKat]);

    // Svislá vodicí linka vlevo od odsazených položek — bez ní se při víc
    // otevřených úrovních ztrácí, co pod co patří. Kreslí se absolutně, takže
    // nesahá na layout řádků ani na drop zóny.
    const VodiciLinka = ({ depth = 0 }) => (
        <span aria-hidden style={{
            position: 'absolute', left: 8 + depth * INDENT + 6, top: 0, bottom: 0, width: 1,
            background: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)',
            pointerEvents: 'none',
        }} />
    );

    const rowFor = (it, depthPad = 0, eff = null) => {
        const key = recVariantKey(it);
        const active = activeKey ? key === activeKey : activeId === it.id;
        const grp = recGrouped?.get(key) ?? [];
        const varCount = grp.length;
        // Řádek zastupuje celou skupinu variant — bez jejich názvů vypadají
        // dvě skupiny téhož jídla v různých kategoriích jako duplikát.
        const varianty = grp.map(r => r.varianta).filter(Boolean);

        const onClick = (e) => (onKlikRadek ? onKlikRadek(e, it.id, () => onSelect(it.id)) : onSelect(it.id));
        // Vybraný řádek táhne celý výběr, nevybraný jen sám sebe.
        const onDragStartItem = canEdit ? ((e, rec) => {
            const ids = vybrane?.has(rec.id) ? [...vybrane] : [rec.id];
            setDragItemIds(ids);
            setDragCatId(null);
            nastavNahledTazeni(e, ids.length);
        }) : undefined;
        const selected = !!vybrane?.has(it.id);

        // Karty materiálu kreslí vlastní, jednodušší řádek (materiál nemá varianty ani
        // kalkulaci) — strom kategorií je jinak pro oba moduly stejný.
        if (renderRow) return renderRow({ item: it, active, selected, depthPad, onClick, onDragStartItem });

        return (
            <RecipeListRow key={it.id} rec={it} active={active} selected={selected}
                varCount={varCount} varianty={varianty} depthPad={depthPad}
                onClick={onClick} siroky={siroky}
                prodej={prodejMap?.[it.id] || null} sloupce={sloupce}
                kanalPokladna={!!eff?.pokladna}
                kanalEshop={!!eff?.eshop && it.eshop_viditelny !== false}
                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                onDragStartItem={onDragStartItem} />
        );
    };

    const handleDropOn = (catId) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCat(null);

        // Přetažení KATEGORIE → změna nadřazenosti (pod jinou / na kořen)
        if (dragCatId != null && dragItemIds == null) {
            const src = dragCatId;
            setDragCatId(null);
            if (onReorderCat && dragPoloha !== 'do' && catId !== BEZ_KAT && catId !== ROOT_DROP) {
                if (catId !== src) onReorderCat(src, catId, dragPoloha);
                return;
            }
            const target = (catId === BEZ_KAT || catId === ROOT_DROP) ? null : catId;
            if (target === src) return;                      // sama na sebe
            if (target != null) {
                const node = findNodeById(strom, src);
                if (node && subtreeIds(node).has(target)) return; // nelze pod vlastního potomka
            }
            onReparentCat?.(src, target);
            return;
        }

        // Přetažení POLOŽEK → přeřazení do kategorie (jedna i celý výběr)
        const fallback = parseInt(e.dataTransfer.getData('text/plain'));
        const ids = dragItemIds ?? (fallback ? [fallback] : []);
        setDragItemIds(null);
        if (ids.length) onMoveItems?.(ids, catId === BEZ_KAT ? null : catId);
    };

    // Odznak viditelnosti kategorie (efektivní — se zohledněním předků)
    const visBadge = (eff) => {
        if (!eff.pokladna && !eff.eshop && !eff.kiosek) {
            return <span title="Jen v adminu (receptáři)" className="flex flex-shrink-0"><Lock size={12} style={{ color: mutedColor, opacity: 0.5 }} /></span>;
        }
        return (
            <span className="flex items-center gap-1 flex-shrink-0"
                title={['Viditelné:', eff.pokladna && 'pokladna', eff.eshop && 'e-shop', eff.kiosek && 'kiosek'].filter(Boolean).join(' ')}>
                {eff.pokladna && <Store size={13} style={{ color: '#F59E0B' }} />}
                {eff.eshop && <Globe size={13} style={{ color: '#3B82F6' }} />}
                {eff.kiosek && <Tablet size={13} style={{ color: '#8B5CF6' }} />}
            </span>
        );
    };

    const renderNode = (node, depth, parentEff = { pokladna: true, eshop: true, kiosek: true }) => {
        const count = countMap.get(node.id) || 0;
        if (searchActive && count === 0) return null;
        const isOpen = searchActive || expanded.has(node.id);
        const direct = itemsByKat.get(node.id) || [];
        const isDropTarget = dragOverCat === node.id;
        const cara = isDropTarget && dragCatId != null && dragCatId !== node.id && dragPoloha !== 'do' ? dragPoloha : null;
        const vnoreni = isDropTarget && !cara;
        const beingDragged = dragCatId === node.id;
        // Efektivní viditelnost — skrytý předek skryje potomka (dědičnost)
        const eff = {
            pokladna: !!parentEff.pokladna && !!node.viditelne_pokladna,
            eshop: !!parentEff.eshop && !!node.viditelne_eshop,
            kiosek: !!parentEff.kiosek && !!node.viditelne_kiosek,
        };

        return (
            // V přehledu (karty) dostanou hlavní kategorie vzduch nad sebou —
            // jinak se velké karty slijí do jednoho pásu bez rytmu.
            <div key={node.id} style={siroky && depth === 0 ? { marginTop: 12 } : undefined}>
                <div className="group flex items-center transition-colors"
                    draggable={canEdit}
                    onDragStart={canEdit ? (e => {
                        setDragCatId(node.id); setDragItemIds(null);
                        e.dataTransfer.effectAllowed = 'move';
                        try { e.dataTransfer.setData('text/plain', 'cat:' + node.id); } catch { /* noop */ }
                    }) : undefined}
                    onDragEnd={canEdit ? (() => { setDragCatId(null); setDragOverCat(null); }) : undefined}
                    style={{
                        gap: siroky ? GRID.mezera : 10,
                        padding: siroky ? '8px 10px' : '7px 10px',
                        // Bez úchytu přetahování začínal řádek o dva pixely jinde
                        // než pole hledání nad ním a levá hrana se třepila.
                        paddingLeft: (siroky ? 10 : (kompaktni ? 10 : 8)) + depth * INDENT,
                        borderLeft: siroky ? '3px solid transparent' : undefined,
                        opacity: beingDragged ? 0.4 : 1,
                        /* Podbarvení kořenové kategorie je pomůcka pro myš na husté
                           tabulce. Na mobilu je kategorie poznat podle barevné
                           ikony a podbarvený pruh přes záři jen vypadá jako sklo. */
                        position: 'relative',
                        background: vnoreni
                            ? (isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)')
                            : aktivniKategorieId === node.id
                                ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)')
                                : (depth === 0 && !kompaktni ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent'),
                        outline: vnoreni ? '1px dashed #F59E0B' : 'none',
                        boxShadow: aktivniKategorieId === node.id ? 'inset 3px 0 0 #F59E0B' : undefined,
                    }}
                    onDragOver={canEdit ? (e => {
                        e.preventDefault(); e.stopPropagation(); setDragOverCat(node.id);
                        // Horní a dolní čtvrtina řádku = řazení, prostředek = vnoření.
                        if (onReorderCat && dragCatId != null) {
                            const r = e.currentTarget.getBoundingClientRect(), y = (e.clientY - r.top) / r.height;
                            setDragPoloha(y < 0.28 ? 'nad' : (y > 0.72 ? 'pod' : 'do'));
                        } else setDragPoloha('do');
                    }) : undefined}
                    onDragLeave={canEdit ? (() => setDragOverCat(c => c === node.id ? null : c)) : undefined}
                    onDrop={canEdit ? handleDropOn(node.id) : undefined}>
                    {cara && (
                        <span aria-hidden="true" style={{ position: 'absolute', zIndex: 2, pointerEvents: 'none', height: 3, borderRadius: 2, background: '#F59E0B',
                            left: (siroky ? 10 : (kompaktni ? 10 : 8)) + depth * INDENT, right: 6, [cara === 'nad' ? 'top' : 'bottom']: -2,
                            boxShadow: '0 0 6px rgba(245,158,11,0.6)' }}>
                            <span style={{ position: 'absolute', left: -4, top: -3, width: 9, height: 9, borderRadius: '50%', border: '2px solid #F59E0B', background: 'var(--surface-base, #111)' }} />
                        </span>
                    )}

                    {siroky ? (() => {
                        // Přehled: kategorie drží stejné sloupce jako položky —
                        // souhrn (průměr marže, průměrný náklad, prodej) tam, kde
                        // má položka svoje číslo. Akce se ukazují až pod myší, ať
                        // řádek nešumí ikonami.
                        const souhrn = souhrnMap.get(node.id) || {};
                        const podkategorii = (node.children || []).length;
                        const popisky = [
                            `${count} ${count === 1 ? 'položka' : (count >= 2 && count <= 4 ? 'položky' : 'položek')}`,
                            podkategorii ? `${podkategorii} ${podkategorii === 1 ? 'podkategorie' : (podkategorii <= 4 ? 'podkategorie' : 'podkategorií')}` : null,
                        ].filter(Boolean);

                        return (
                            <>
                                <div className="flex items-center justify-center" style={{ width: GRID.uchyt, flexShrink: 0 }}>
                                    <button onClick={() => toggle(node.id)} title={isOpen ? 'Sbalit' : 'Rozbalit'}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                                        <ChevronRight size={16} style={{ color: mutedColor, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                    </button>
                                </div>
                                {sloupce?.nahled !== false && (
                                    <div style={{ width: GRID.nahled, flexShrink: 0, display: 'flex' }}>
                                        <RecipeKatIcon slozka ikona={node.ikona} kategorieNazev={node.nazev} kategorieBarva={node.barva} size={depth === 0 ? 34 : 28} radius={7} />
                                    </div>
                                )}
                                <div className="min-w-0 cursor-pointer" style={{ flex: `${GRID.polozkaRust} 1 0`, minWidth: GRID.polozkaMin }}
                                    onClick={() => klikNaKategorii(node)}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className={`${depth === 0 ? 'text-[14px]' : 'text-[13px]'} font-semibold truncate`} style={{ color: 'var(--text-primary)' }}>{node.nazev}</span>
                                        {zobrazitViditelnost && visBadge(eff)}
                                        <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {onAiAnalyza && (
                                                <button onClick={(e) => { e.stopPropagation(); onAiAnalyza(node); }} title="AI analýza kategorie"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, display: 'flex' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#A855F7'}
                                                    onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                                                    <Sparkles size={14} />
                                                </button>
                                            )}
                                            {canEdit && (
                                                <button onClick={(e) => { e.stopPropagation(); openEdit(node); }} title="Upravit kategorii"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, display: 'flex' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                                                    onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                                                    <Pencil size={14} />
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    <div className="text-[11px] truncate" style={{ color: mutedColor, marginTop: 1 }}>{popisky.join(' · ')}</div>
                                </div>
                                {renderKategorieBunky ? renderKategorieBunky({ node, polozky: podstromMap?.get(node.id) || [] }) : (
                                    <>
                                        {sloupce?.kanaly !== false && <div style={{ flex: '1 1 auto', minWidth: GRID.kanaly }} />}
                                        <NakladCenaBunka naklad={souhrn.naklad} cena={null} souhrn mutedColor={mutedColor} isDark={isDark} />
                                        <MarzeBunka marze={souhrn.marze} souhrn mutedColor={mutedColor} isDark={isDark} />
                                        {sloupce?.prodej !== false && <ProdejBunka prodej={souhrn.prodej} souhrn mutedColor={mutedColor} isDark={isDark} />}
                                        {sloupce?.revize !== false && <div style={{ width: GRID.revize, flexShrink: 0 }} />}
                                        <StavBunka problemu={validaceMap.get(node.id)?.red || 0} mutedColor={mutedColor} />
                                    </>
                                )}
                            </>
                        );
                    })() : (
                        <>
                            {/* Úchyt přetahování na mobilu ne: HTML5 drag & drop
                                na dotyku nefunguje, takže by to byl mrtvý ovladač,
                                který jen ukrajuje ~20 px z levé hrany každého
                                řádku. Přesouvá se na desktopu, kde je plná editace. */}
                            {canEdit && !kompaktni && (
                                <GripVertical size={13} className="opacity-20 group-hover:opacity-60"
                                    style={{ color: mutedColor, cursor: 'grab', flexShrink: 0, marginLeft: -4 }}
                                    title="Přetáhni pro přesun pod jinou kategorii" />
                            )}
                            <div onClick={() => klikNaKategorii(node)} className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                                <RecipeKatIcon slozka ikona={node.ikona} kategorieNazev={node.nazev} kategorieBarva={node.barva} size={depth === 0 ? 34 : 28} radius={7} />
                                <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{node.nazev}</span>
                            </div>
                            {onAiAnalyza && (
                                <button onClick={(e) => { e.stopPropagation(); onAiAnalyza(node); }} title="AI analýza kategorie"
                                    className="flex items-center justify-center rounded-md"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, flexShrink: 0 }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#A855F7'}
                                    onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                                    <Sparkles size={14} />
                                </button>
                            )}
                            {/* Tužka jen na desktopu. Měla 22×22 px (polovina
                                dotykového minima) a zvýrazňovala se přes hover,
                                který na dotyku nenastane — myšový ovladač. Úprava
                                kategorie je navíc správcovská akce; mobil je na
                                zobrazení a kontrolu, ne na přestavbu stromu. */}
                            {canEdit && !kompaktni && (
                                <button onClick={() => openEdit(node)} title="Upravit kategorii"
                                    className="flex items-center justify-center rounded-md"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, flexShrink: 0 }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                                    <Pencil size={14} />
                                </button>
                            )}
                            {zobrazitViditelnost && visBadge(eff)}
                            <RecipeValidaceCounts counts={validaceMap.get(node.id)} />
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: mutedColor, flexShrink: 0, minWidth: 26, textAlign: 'center' }}>{count}</span>
                            {/* Šipka je na mobilu u pravé hrany, kam palec chodí
                                nejčastěji — dostane plochu na prst. Rozbalit jde
                                i klepnutím na název, tohle je druhá cesta. */}
                            <button onClick={() => toggle(node.id)} aria-label={isOpen ? 'Sbalit' : 'Rozbalit'}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: 0, width: kompaktni ? 40 : undefined, height: kompaktni ? 44 : undefined,
                                    marginRight: kompaktni ? -8 : 0,
                                }}>
                                <ChevronRight size={kompaktni ? 18 : 16} style={{ color: mutedColor, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                            </button>
                        </>
                    )}
                </div>

                {isOpen && (
                    <div style={{ position: 'relative' }}>
                        <VodiciLinka depth={depth} />
                        {direct.map(it => rowFor(it, (depth + 1) * INDENT - 2, eff))}
                        {(node.children || []).map(ch => renderNode(ch, depth + 1, eff))}
                    </div>
                )}
            </div>
        );
    };

    const bez = itemsByKat.get(BEZ_KAT) || [];
    // Kořenovou kategorii nemá smysl přesouvat na kořen — zóna by jen překážela.
    const taznaJeKorenova = dragCatId != null && strom.some(n => n.id === dragCatId);

    return (
        // dragend uklidí stav i po puštění mimo cíl — jinak by zůstala viset
        // zvýrazněná drop zóna a příště by se pustila stará položka.
        <div onDragEnd={() => { setDragItemIds(null); setDragCatId(null); setDragOverCat(null); }}>
            {/* Drop zóna pro přesun kategorie na kořenovou úroveň — jen při tažení
                podkategorie. Kreslí se jako overlay v nulově vysokém sticky obalu:
                kdyby zabírala místo v toku, odsunula by v okamžiku začátku tažení
                celý seznam o svou výšku dolů. Odscrollovanému seznamu to prohlížeč
                srovná kotvením scrollu, ale úplně nahoře (scrollTop = 0) není co
                kotvit — řádek, na který uživatel mířil, by utekl a puštění by
                spadlo sem, na „přesun na kořen". U kořenové kategorie je to navíc
                prázdná operace, takže to vypadalo, že drag&drop nefunguje.
                Sticky má i tu výhodu, že je zóna po ruce i v odrolovaném seznamu. */}
            {dragCatId != null && !taznaJeKorenova && (
                <div style={{ position: 'sticky', top: 0, height: 0, zIndex: 5 }}>
                    <div onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverCat(ROOT_DROP); }}
                        onDragLeave={() => setDragOverCat(c => c === ROOT_DROP ? null : c)}
                        onDrop={handleDropOn(ROOT_DROP)}
                        className="flex items-center gap-2 text-[12px] font-semibold"
                        style={{
                            position: 'absolute', left: 6, right: 6, top: 3,
                            padding: '5px 10px', borderRadius: 6,
                            border: `1px dashed ${dragOverCat === ROOT_DROP ? '#F59E0B' : (isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)')}`,
                            background: dragOverCat === ROOT_DROP
                                ? (isDark ? 'rgba(245,158,11,0.22)' : 'rgba(245,158,11,0.14)')
                                : (isDark ? 'rgba(24,22,32,0.96)' : 'rgba(255,255,255,0.96)'),
                            color: mutedColor,
                            boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.45)' : '0 6px 16px rgba(0,0,0,0.15)',
                        }}>
                        <ChevronRight size={13} style={{ transform: 'rotate(-90deg)' }} /> Přesunout na kořenovou úroveň
                    </div>
                </div>
            )}

            {strom.map(n => renderNode(n, 0))}

            {/* Bez kategorie — drop cíl pro odřazení */}
            {bez.length > 0 && (() => {
                const isOpen = searchActive || expanded.has(BEZ_KAT);
                const isDropTarget = dragOverCat === BEZ_KAT;
                return (
                    <div>
                        {/* Rozbaluje se klepnutím kamkoli do řádku, ne jen na
                            13px šipku — na prst se do ní nedá trefit. Řádek není
                            <button>, protože je zároveň drop cílem a nese další
                            ovládání; role a klávesnice se proto doplňují ručně. */}
                        <div className="flex items-center gap-1.5" role="button" tabIndex={0}
                            aria-expanded={isOpen} aria-label={`Bez kategorie — ${bez.length}`}
                            onClick={() => toggle(BEZ_KAT)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(BEZ_KAT); } }}
                            style={{
                                padding: kompaktni ? '0 12px' : '6px 8px',
                                minHeight: kompaktni ? 44 : undefined,
                                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                                background: isDropTarget ? (isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)') : 'transparent',
                                outline: isDropTarget ? '1px dashed #F59E0B' : 'none',
                            }}
                            onDragOver={canEdit ? (e => { e.preventDefault(); setDragOverCat(BEZ_KAT); }) : undefined}
                            onDragLeave={canEdit ? (() => setDragOverCat(c => c === BEZ_KAT ? null : c)) : undefined}
                            onDrop={canEdit ? handleDropOn(BEZ_KAT) : undefined}>
                            <ChevronRight size={kompaktni ? 15 : 13} style={{ color: mutedColor, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                            <FolderOpen size={kompaktni ? 14 : 12} style={{ color: mutedColor, flexShrink: 0 }} />
                            <span className={`${kompaktni ? 'text-[13px]' : 'text-[12px]'} font-semibold flex-1 uppercase tracking-wide`} style={{ color: secondaryColor }}>Bez kategorie</span>
                            <RecipeValidaceCounts counts={aggregateValidace(bez)} />
                            <span className="text-[11px] font-medium" style={{ color: mutedColor }}>{bez.length}</span>
                        </div>
                        {isOpen && <div style={{ position: 'relative' }}><VodiciLinka />{bez.map(it => rowFor(it, INDENT - 2))}</div>}
                    </div>
                );
            })()}

            {/* Archív položek — neaktivní */}
            {archiv.length > 0 && (() => {
                const isOpen = searchActive || archivOpen;
                return (
                    <div style={{ marginTop: 4, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                        <button onClick={() => setArchivOpen(o => !o)} className="w-full flex items-center gap-1.5 text-left"
                            style={{
                                padding: kompaktni ? '0 12px' : '7px 8px',
                                minHeight: kompaktni ? 44 : undefined,
                                background: 'none', border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                            }}>
                            <ChevronRight size={kompaktni ? 15 : 13} style={{ color: mutedColor, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                            <Archive size={kompaktni ? 14 : 12} style={{ color: mutedColor, flexShrink: 0 }} />
                            <span className={`${kompaktni ? 'text-[13px]' : 'text-[12px]'} font-semibold flex-1 uppercase tracking-wide`} style={{ color: mutedColor }}>Archív položek</span>
                            <span className="text-[11px] font-medium" style={{ color: mutedColor }}>{archiv.length}</span>
                        </button>
                        {isOpen && <div style={{ opacity: 0.7, position: 'relative' }}><VodiciLinka />{archiv.map(it => rowFor(it, INDENT - 2))}</div>}
                    </div>
                );
            })()}

            {/* Přidat kořenovou kategorii */}
            {canEdit && !skrytPridatKategorii && (
                <button onClick={() => openCreate(null)} className="flex items-center gap-1.5 px-3 py-2 text-[11px] w-full"
                    style={{ color: mutedColor, background: 'none', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'} onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                    <Plus size={13} /> Přidat kategorii
                </button>
            )}

            {/* Modal úpravy / vytvoření kategorie */}
            {modal && (
                <RecipeCategoryModal
                    key={`${modal.mode}-${modal.node?.id ?? 'new'}-${modal.parentId ?? 'root'}`}
                    state={modal}
                    isDark={isDark}
                    onClose={() => setModal(null)}
                    onSave={(id, patch) => onCatSave?.(id, patch)}
                    onCreate={(parentId, data) => onCatAdd?.(parentId, data)}
                    onDelete={(id) => onCatDelete?.(id)}
                    onAddSub={(parentNode) => openCreate(parentNode)}
                    zobrazitViditelnost={zobrazitViditelnost}
                />
            )}
        </div>
    );
}
